import { useState, useMemo, useRef, useCallback } from 'react';

import PropTypes from 'prop-types';

import { useSignImageQueries } from '@features/approval/hooks';
import safeToBase64Image from '@shared/lib/safeToBase64Image';

import styles from './MobileApprovalLineCarousel.module.scss';

/**
 * 모바일 결재라인 캐러셀 컴포넌트
 * - 결재자 목록을 스와이프로 표시
 * - 한 화면에 4명씩 표시
 * - 좌우 페이징 버튼 및 인디케이터
 */
function MobileApprovalLineCarousel({ documentInfo, docIdx }) {
  const [currentPage, setCurrentPage] = useState(0);
  const carouselRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const touchStartTime = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  // 결재자 목록 구성 (order 순서대로 정렬)
  const approvers = useMemo(
    function () {
      if (!documentInfo?.approvalLine) {
        return [];
      }

      const line = documentInfo.approvalLine;
      const result = [];

      // 결재자 추가 (approval1-4 → sign_2~sign_5)
      const approvals = ['approval1', 'approval2', 'approval3', 'approval4'];
      approvals.forEach(function (key, index) {
        if (line[key] && line[key].enumber) {
          result.push({
            type: 'signer',
            typeLabel: '결재',
            name: line[key].name || '-',
            position: line[key].position || '',
            state: line[key].state || 1,
            order: line[key].order || 0,
            signKey: `sign_${index + 2}`, // sign_2, sign_3, sign_4, sign_5
          });
        }
      });

      // 검토자 추가 (review1-4 → check_1~check_4)
      const reviewers = ['review1', 'review2', 'review3', 'review4'];
      reviewers.forEach(function (key, index) {
        if (line[key] && line[key].enumber) {
          result.push({
            type: 'checker',
            typeLabel: '검토',
            name: line[key].name || '-',
            position: line[key].position || '',
            state: line[key].state || 1,
            order: line[key].order || 0,
            signKey: `check_${index + 1}`, // check_1, check_2, check_3, check_4
          });
        }
      });

      // 합의자 추가 (agreement1 → agreement)
      if (line.agreement1 && line.agreement1.enumber) {
        result.push({
          type: 'agreement',
          typeLabel: '합의',
          name: line.agreement1.name || '-',
          position: line.agreement1.position || '',
          state: line.agreement1.state || 1,
          order: line.agreement1.order || 0,
          signKey: 'agreement', // 합의자는 agreement
        });
      }

      // order 순서로 정렬 (order가 0인 경우는 맨 뒤로)
      result.sort(function (a, b) {
        const orderA = a.order || 0;
        const orderB = b.order || 0;
        if (orderA === 0 && orderB === 0) {
          return 0;
        }
        if (orderA === 0) {
          return 1;
        }
        if (orderB === 0) {
          return -1;
        }
        return orderA - orderB;
      });

      return result;
    },
    [documentInfo?.approvalLine],
  );

  // 서명 이미지 조회를 위한 서명자 목록 생성
  const signersToFetch = useMemo(
    function () {
      return approvers
        .filter(function (approver) {
          return approver.state === 3 && approver.signKey;
        })
        .map(function (approver) {
          return {
            key: approver.signKey,
            enumber: approver.enumber,
          };
        });
    },
    [approvers],
  );

  // 서명 이미지 조회
  const { data: rawSignImages } = useSignImageQueries(docIdx, signersToFetch);

  // 서명 이미지 데이터를 base64로 변환
  const signImages = useMemo(
    function () {
      const images = {};
      Object.keys(rawSignImages || {}).forEach(function (key) {
        images[key] = safeToBase64Image(rawSignImages[key]);
      });
      return images;
    },
    [rawSignImages],
  );

  // 페이지당 4명씩 그룹화
  const ITEMS_PER_PAGE = 4;
  const totalPages = Math.ceil(approvers.length / ITEMS_PER_PAGE);

  const pagedApprovers = useMemo(
    function () {
      const pages = [];
      for (let i = 0; i < approvers.length; i += ITEMS_PER_PAGE) {
        pages.push(approvers.slice(i, i + ITEMS_PER_PAGE));
      }
      return pages;
    },
    [approvers],
  );

  // 페이지 변경 핸들러
  function handlePrevPage() {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  }

  function handleNextPage() {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  }

  // 터치 스와이프 핸들러
  const handleTouchStart = useCallback(function (e) {
    touchStartX.current = e.touches[0].clientX;
    touchStartTime.current = Date.now();
    setIsDragging(true);
    setDragOffset(0);
  }, []);

  const handleTouchMove = useCallback(
    function (e) {
      if (!isDragging) {
        return;
      }

      const currentX = e.touches[0].clientX;
      const diff = touchStartX.current - currentX;

      // 드래그 중 실시간으로 오프셋 업데이트
      // 첫 페이지에서 오른쪽으로 드래그하거나, 마지막 페이지에서 왼쪽으로 드래그하는 경우 제한
      if (currentPage === 0 && diff < 0) {
        // 첫 페이지에서 오른쪽으로 드래그 (저항 효과)
        setDragOffset(-diff * 0.3);
      } else if (currentPage === totalPages - 1 && diff > 0) {
        // 마지막 페이지에서 왼쪽으로 드래그 (저항 효과)
        setDragOffset(-diff * 0.3);
      } else {
        setDragOffset(-diff);
      }

      touchEndX.current = currentX;
    },
    [isDragging, currentPage, totalPages],
  );

  const handleTouchEnd = useCallback(
    function () {
      setIsDragging(false);

      const diff = touchStartX.current - touchEndX.current;
      const timeDiff = Date.now() - touchStartTime.current;
      const velocity = Math.abs(diff) / timeDiff; // 픽셀/밀리초
      const minSwipeDistance = 50;
      const minVelocity = 0.3; // 빠른 스와이프를 위한 최소 속도

      // 스와이프 거리 또는 속도가 충분한 경우 페이지 변경
      const shouldSwipe =
        Math.abs(diff) > minSwipeDistance || (Math.abs(diff) > 30 && velocity > minVelocity);

      if (shouldSwipe) {
        if (diff > 0 && currentPage < totalPages - 1) {
          // 왼쪽으로 스와이프 -> 다음 페이지
          setCurrentPage(currentPage + 1);
        } else if (diff < 0 && currentPage > 0) {
          // 오른쪽으로 스와이프 -> 이전 페이지
          setCurrentPage(currentPage - 1);
        }
      }

      // 드래그 오프셋 초기화
      setDragOffset(0);
      touchStartX.current = 0;
      touchEndX.current = 0;
      touchStartTime.current = 0;
    },
    [currentPage, totalPages],
  );

  if (approvers.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.sectionTitle}>결재라인</h3>

      <div className={styles.carouselWrapper}>
        {/* 좌측 페이징 버튼 */}
        {currentPage > 0 && (
          <button
            type="button"
            className={`${styles.pageButton} ${styles.prevButton}`}
            onClick={handlePrevPage}
            aria-label="이전 페이지"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="#C1C1C1"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {/* 캐러셀 컨테이너 */}
        <div
          ref={carouselRef}
          className={styles.carousel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* 모든 페이지를 가로로 나열 */}
          <div
            className={styles.pagesContainer}
            style={{
              transform: `translateX(calc(-${currentPage * 100}% + ${dragOffset}px))`,
              transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {pagedApprovers.map(function (page, pageIndex) {
              return (
                <div key={`page-${pageIndex}`} className={styles.page}>
                  <div className={styles.approverGrid}>
                    {page.map(function (approver, index) {
                      const signImage = approver.signKey ? signImages[approver.signKey] : null;
                      const isCompleted = approver.state === 3;

                      return (
                        <div key={`approver-${pageIndex}-${index}`} className={styles.approverItem}>
                          {/* 상태 원형 */}
                          <div
                            className={`${styles.statusCircle} ${getStateCircleClass(approver.state)}`}
                          >
                            {isCompleted && signImage ? (
                              <img src={signImage} alt="서명" className={styles.signImage} />
                            ) : (
                              <span className={styles.statusText}>
                                {getStateText(approver.state)}
                              </span>
                            )}
                          </div>

                          {/* 이름 */}
                          <span className={styles.approverName}>{approver.name}</span>

                          {/* 역할 배지 */}
                          <div className={styles.roleBadge}>
                            <span className={styles.roleText}>{approver.typeLabel}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 우측 페이징 버튼 */}
        {currentPage < totalPages - 1 && (
          <button
            type="button"
            className={`${styles.pageButton} ${styles.nextButton}`}
            onClick={handleNextPage}
            aria-label="다음 페이지"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M7.5 5L12.5 10L7.5 15"
                stroke="#C1C1C1"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      {/* 페이지 인디케이터 */}
      {totalPages > 1 && (
        <div className={styles.pageIndicator}>
          {Array.from({ length: totalPages }).map(function (_, index) {
            return (
              <span
                key={`indicator-${index}`}
                className={`${styles.dot} ${index === currentPage ? styles.activeDot : ''}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * 결재 상태에 따른 원형 스타일 클래스
 */
function getStateCircleClass(state) {
  switch (state) {
    case 2:
      return styles.stateInProgress;
    case 3:
      return styles.stateCompleted;
    case 4:
      return styles.stateRejected;
    default:
      return styles.statePending;
  }
}

/**
 * 결재 상태 텍스트
 */
function getStateText(state) {
  switch (state) {
    case 1:
      return '결재\n대기';
    case 2:
      return '결재중';
    case 3:
      return null; // 완료 상태는 서명 이미지 표시
    case 4:
      return '반려';
    default:
      return '결재\n대기';
  }
}

MobileApprovalLineCarousel.propTypes = {
  documentInfo: PropTypes.shape({
    approvalLine: PropTypes.object,
  }),
  docIdx: PropTypes.number,
};

MobileApprovalLineCarousel.defaultProps = {
  documentInfo: {},
  docIdx: null,
};

export default MobileApprovalLineCarousel;
